import { useEffect, useMemo, useState } from 'react';
import { Form, Modal } from 'antd';
import PopupHeader from './PopupHeader';
import PopupBody from './PopupBody';
import PopupFooter from './PopupFooter';
import PopupFields from './PopupFields';
import { usePopupOptions } from './hooks/usePopupOptions';
import { usePopupSearch } from './hooks/usePopupSearch';
import { usePopupSubmit } from './hooks/usePopupSubmit';
import { getDefaultContext } from './utils/popupUtils';

/**
 * Config-driven modal popup for ATS/CRM/ERP bulk and single-record actions.
 *
 * @param {Object} props
 * @param {boolean} [props.open=true]
 * @param {Object} props.config - Popup configuration object (from popupConfigs.js or project-specific).
 * @param {Object} [props.context={}] - Runtime context: selectedIds, selectedRecords, moduleName, etc.
 * @param {Function} props.onClose - Called when the popup should close (navigate away / hide).
 * @param {Function} [props.onSubmit] - Fallback submit handler used when api.submit is absent.
 * @param {Object} [props.api={}] - API handlers: getOptions, searchOptions, getMentionOptions,
 *                                  loadExtraData, submit, deleteItems, onOk.
 */
export default function DynamicPopup({
  open = true,
  config,
  context = {},
  onClose,
  onSubmit,
  api = {},
}) {
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState({});
  const [removedIds, setRemovedIds] = useState([]);

  const popupContext = useMemo(() => getDefaultContext(context), [context]);
  const { selectedIds = [], selectedRecords = [], moduleName = '' } = popupContext;

  const resolvedIds = useMemo(
    () =>
      selectedIds.map((id, i) => {
        const record = selectedRecords[i];
        return record?.id ?? record?._id ?? id;
      }),
    [selectedIds, selectedRecords],
  );

  const fields = useMemo(() => {
    if (!config) return [];
    return typeof config.getFields === 'function'
      ? config.getFields(popupContext)
      : (config.fields ?? []);
  }, [config, popupContext]);

  const initialValues = useMemo(() => {
    const values = {};
    fields.forEach((field) => {
      if (field.initialValueKey && popupContext[field.initialValueKey] !== undefined) {
        values[field.name] = popupContext[field.initialValueKey];
      }
    });
    return values;
  }, [fields, popupContext]);

  const { dynamicOptions, setDynamicOptions, extraData } = usePopupOptions({
    open,
    config,
    api,
    fields,
    popupContext,
    formValues,
  });

  const { mentionOptions, getSearchHandler, getMentionSearchHandler } = usePopupSearch({
    api,
    popupContext,
    setDynamicOptions,
  });

  function handleClose() {
    if (typeof onClose === 'function') onClose();
  }

  const { handleOk, confirming, deleteError, resetSubmitState } = usePopupSubmit({
    form,
    config,
    api,
    popupContext,
    resolvedIds,
    dynamicOptions,
    extraData,
    removedIds,
    fields,
    onClose: handleClose,
    onSubmit,
  });

  // Reset all local state each time the popup opens to avoid stale values.
  // Seed formValues from initialValues (not {}) — antd's Form onValuesChange
  // only fires on user interaction, so a field pre-filled via initialValueKey
  // (e.g. "Shortlisted" locking hiringStage) would otherwise never count
  // toward disabledUntilFilled/allRequiredFilled until the user touched it.
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setFormValues(initialValues ?? {});
    setRemovedIds(popupContext.removedIds ?? []);
    resetSubmitState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValues]);

  // Some popups (Source Candidates' "Shortlisted"/"Quick Submit") already know
  // everything they need from context (jobId, locked hiringStage) and resolve
  // to zero fields — config.autoSubmitWhenNoFields skips the confirm dialog
  // entirely and runs the action the instant the popup would have opened.
  const shouldAutoSubmit = Boolean(config?.autoSubmitWhenNoFields) && fields.length === 0;
  useEffect(() => {
    if (!open || !shouldAutoSubmit) return;
    handleOk();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shouldAutoSubmit]);

  const count = selectedIds.length;
  const singularLabel = String(moduleName).replace(/s$/i, '');
  const itemLabel = count === 1 ? singularLabel : moduleName;
  const firstRecord = selectedRecords[0] ?? {};

  const titleText = config?.getTitleText
    ? config.getTitleText(count, itemLabel, firstRecord, popupContext)
    : config?.title;

  const itemTitle =
    count === 1
      ? firstRecord.jobTitle ??
        firstRecord.title ??
        firstRecord.name ??
        firstRecord.fullName ??
        firstRecord.candidateName ??
        null
      : null;

  const bodyText = config?.getBodyText
    ? config.getBodyText(count, singularLabel, itemLabel, itemTitle, popupContext)
    : config?.bodyText ?? null;

  const note =
    typeof config?.getNote === 'function' ? config.getNote(popupContext) : config?.note;

  const requiredFields = fields.filter((f) => f.required);
  const allRequiredFilled = requiredFields.every((f) => {
    const v = formValues[f.name];
    if (v === undefined || v === null || v === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return false;
    return true;
  });
  const okDisabled = config?.disabledUntilFilled && !allRequiredFilled;

  if (!config || !open || shouldAutoSubmit) return null;

  return (
    <Modal
      open={open}
      title={<PopupHeader titleText={titleText} titleIcon={config.titleIcon} />}
      okText={config.okText || 'OK'}
      cancelText={config.cancelText ?? 'Cancel'}
      onOk={handleOk}
      onCancel={handleClose}
      okButtonProps={{ danger: config.okButtonDanger, disabled: okDisabled, loading: confirming }}
      cancelButtonProps={{ disabled: confirming }}
      closable={!confirming}
      maskClosable={false}
      width={config.width ?? 480}
      centered
      className={`dynamic-popup${config.className ? ` ${config.className}` : ''}`}
    >
      <PopupBody
        note={note}
        description={config.description}
        showSelectedCount={config.showSelectedCount}
        count={count}
        itemLabel={itemLabel}
        bodyText={bodyText}
        deleteError={deleteError}
      />
      <PopupFooter footerNote={config.footerNote} />
      <PopupFields
        form={form}
        fields={fields}
        initialValues={initialValues}
        dynamicOptions={dynamicOptions}
        popupContext={popupContext}
        extraData={extraData}
        removedIds={removedIds}
        setRemovedIds={setRemovedIds}
        showAlreadyAssigned={config.showAlreadyAssigned}
        onValuesChange={setFormValues}
        getSearchHandler={getSearchHandler}
        getMentionSearchHandler={getMentionSearchHandler}
        mentionOptions={mentionOptions}
      />
    </Modal>
  );
}
